import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import { type LeaveType, createLeave } from "./api";
import { CustomSelect } from "@/components/ui/CustomSelect";

const TYPES: LeaveType[] = ["VACATION", "SICKNESS", "PERSONAL", "PATERNITY", "OTHER"];

const CALENDAR_SVG = (
  <svg viewBox="0 0 24 24" focusable="false">
    <path
      d="M7 2a1 1 0 0 1 1 1v1h8V3a1 1 0 1 1 2 0v1h1.5A2.5 2.5 0 0 1 22 6.5v13A2.5 2.5 0 0 1 19.5 22h-15A2.5 2.5 0 0 1 2 19.5v-13A2.5 2.5 0 0 1 4.5 4H6V3a1 1 0 0 1 1-1Zm0 6a1 1 0 0 0 0 2h10a1 1 0 0 0 0-2H7Zm-2.5-2a.5.5 0 0 0-.5.5V9h16V6.5a.5.5 0 0 0-.5-.5h-15Z"
      fill="currentColor"
    />
  </svg>
);

export function NewLeavePage() {
  const { t, i18n } = useTranslation();
  const locale = i18n.language?.startsWith("es") ? "es-ES" : "en-US";
  const navigate = useNavigate();
  const qc = useQueryClient();

  const [type, setType] = useState<LeaveType>("VACATION");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: createLeave,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["leaves-my"] });
      navigate("/leaves/my");
    },
    onError: () => setError(t("leaves.new.error_generic")),
  });

  const days = useMemo(() => computeDays(startDate, endDate), [startDate, endDate]);
  const datesValid = !startDate || !endDate || startDate <= endDate;

  useEffect(() => {
    if (!datesValid) setError(t("leaves.new.date_error"));
    else if (error === t("leaves.new.date_error")) setError(null);
  }, [datesValid, error, t]);

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!startDate || !endDate) return;
    if (!datesValid) {
      setError(t("leaves.new.date_error"));
      return;
    }
    mutation.mutate({
      type,
      start_date: startDate,
      end_date: endDate,
      reason: reason.trim() || null,
    });
  };

  const applyPreset = (kind: "today" | "tomorrow" | "next-week" | "weekend") => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const fmt = (d: Date) =>
      `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    if (kind === "today") {
      setStartDate(fmt(today));
      setEndDate(fmt(today));
    } else if (kind === "tomorrow") {
      const t1 = new Date(today);
      t1.setDate(t1.getDate() + 1);
      setStartDate(fmt(t1));
      setEndDate(fmt(t1));
    } else if (kind === "next-week") {
      // Next Monday → next Friday
      const dow = today.getDay(); // 0=Sun
      const offset = ((8 - dow) % 7) || 7;
      const mon = new Date(today);
      mon.setDate(mon.getDate() + offset);
      const fri = new Date(mon);
      fri.setDate(fri.getDate() + 4);
      setStartDate(fmt(mon));
      setEndDate(fmt(fri));
    } else if (kind === "weekend") {
      const dow = today.getDay();
      // Saturday this week
      const sat = new Date(today);
      const offset = (6 - dow + 7) % 7;
      sat.setDate(sat.getDate() + offset);
      const sun = new Date(sat);
      sun.setDate(sun.getDate() + 1);
      setStartDate(fmt(sat));
      setEndDate(fmt(sun));
    }
  };

  return (
    <div className="leaves-root">
      <h1 className="page-title">{t("leaves.new.title")}</h1>
      <p className="page-subtitle">{t("leaves.new.subtitle")}</p>

      <div className="leave-new-grid">
        <div className="card leave-new-form">
          {error && <p className="flash flash-error">{error}</p>}

          <form onSubmit={onSubmit} noValidate>
            {/* Step 1: Type */}
            <div className="leave-step">
              <div className="leave-step-title">{t("leaves.new.step1")}</div>
              <div className="form-field">
                <label htmlFor="leave_type" className="form-label">
                  {t("leaves.new.type_label")}
                </label>
                <CustomSelect
                  value={type}
                  options={TYPES.map((tk) => ({ value: tk, label: t(`leaves.type.${tk}`) }))}
                  onChange={(v) => setType(v as LeaveType)}
                  allowEmpty={false}
                />
                <p className="summary-card-meta">{t(`leaves.type_desc.${type}`)}</p>
              </div>
            </div>

            {/* Step 2: Dates */}
            <div className="leave-step">
              <div className="leave-step-title">{t("leaves.new.step2")}</div>
              <div className="form-row">
                <div className="form-field" style={{ flex: 1 }}>
                  <label htmlFor="start_date" className="form-label">
                    {t("leaves.new.from")}
                  </label>
                  <div className="date-field">
                    <span className="date-icon" aria-hidden="true">
                      {CALENDAR_SVG}
                    </span>
                    <input
                      id="start_date"
                      name="start_date"
                      type="date"
                      required
                      className="form-input date-input"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                    />
                  </div>
                </div>
                <div className="form-field" style={{ flex: 1 }}>
                  <label htmlFor="end_date" className="form-label">
                    {t("leaves.new.to")}
                  </label>
                  <div className="date-field">
                    <span className="date-icon" aria-hidden="true">
                      {CALENDAR_SVG}
                    </span>
                    <input
                      id="end_date"
                      name="end_date"
                      type="date"
                      required
                      className="form-input date-input"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                    />
                  </div>
                </div>
              </div>
              <div className="leave-presets">
                {(
                  [
                    ["today", "leaves.new.preset.today"],
                    ["tomorrow", "leaves.new.preset.tomorrow"],
                    ["next-week", "leaves.new.preset.next_week"],
                    ["weekend", "leaves.new.preset.weekend"],
                  ] as const
                ).map(([kind, key]) => (
                  <button
                    key={kind}
                    type="button"
                    className="chip chip-toggle"
                    onClick={() => applyPreset(kind)}
                  >
                    {t(key)}
                  </button>
                ))}
              </div>
              {!datesValid && (
                <div className="leave-inline-error">{t("leaves.new.date_error")}</div>
              )}
            </div>

            {/* Step 3: Reason */}
            <div className="leave-step">
              <div className="leave-step-title">{t("leaves.new.step3")}</div>
              <div className="form-field">
                <label htmlFor="reason" className="form-label">
                  {t("leaves.new.reason_label")}
                </label>
                <textarea
                  id="reason"
                  name="reason"
                  rows={3}
                  className="form-textarea"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                />
              </div>
            </div>

            <div className="form-row form-actions">
              <button
                type="submit"
                className="btn btn-primary"
                disabled={mutation.isPending || !datesValid || !startDate || !endDate}
              >
                {mutation.isPending ? t("leaves.new.submitting") : t("leaves.new.submit")}
              </button>
              <Link to="/leaves/my" className="btn btn-secondary">
                {t("leaves.new.cancel")}
              </Link>
            </div>
          </form>
        </div>

        <div className="card leave-summary">
          <div className="summary-card-title">{t("leaves.new.summary_title")}</div>
          <div className="leave-summary-item">
            <span className="leave-summary-label">{t("leaves.new.type_label")}</span>
            <span>{t(`leaves.type.${type}`)}</span>
          </div>
          <div className="leave-summary-item">
            <span className="leave-summary-label">{t("leaves.new.summary_dates")}</span>
            <span>
              {startDate && endDate
                ? `${formatDate(startDate, locale)} → ${formatDate(endDate, locale)}`
                : "—"}
            </span>
          </div>
          <div className="leave-summary-item">
            <span className="leave-summary-label">{t("leaves.new.summary_days")}</span>
            <span>
              {days
                ? `${days} ${days === 1 ? t("leaves.day") : t("leaves.days")}`
                : "—"}
            </span>
          </div>
          <div className="leave-summary-status">
            <span className="status-pill status-requested">
              {t("leaves.new.summary_pill")}
            </span>
          </div>
          <div className="summary-card-meta">{t("leaves.new.summary_hint")}</div>
        </div>
      </div>
    </div>
  );
}

function computeDays(start: string, end: string): number {
  if (!start || !end) return 0;
  if (end < start) return 0;
  const a = new Date(start + "T00:00:00");
  const b = new Date(end + "T00:00:00");
  return Math.floor((b.getTime() - a.getTime()) / (1000 * 60 * 60 * 24)) + 1;
}

function formatDate(iso: string, locale: string): string {
  try {
    return new Intl.DateTimeFormat(locale, { dateStyle: "medium" }).format(
      new Date(iso + "T00:00:00")
    );
  } catch {
    return iso;
  }
}
